import { type ReactNode } from 'react';

import type { FieldValues, UseFormReturn, Path } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';

import { FormField, FormItem, FormLabel, FormControl, FormMessage, Input, Textarea } from '@shared/components/ui';
import { GenericButton } from '@shared/components/ui/custom';

export interface BaseAgentFormValues extends FieldValues {
  name: string;
  agentHost: string;
  description?: string;
}

interface GenericAgentDetailsFormProps<T extends BaseAgentFormValues> {
  form: UseFormReturn<T>;
  onGeneratePackageLink: (data: T) => Promise<void>;
  isLoading: boolean;
  children?: ReactNode;
  onClose: () => void;
}

export const GenericAgentDetailsForm = <T extends BaseAgentFormValues>({
  form,
  onGeneratePackageLink,
  isLoading,
  children,
  onClose,
}: GenericAgentDetailsFormProps<T>) => {
  const namePath = 'name' as Path<T>;
  const hostPath = 'agentHost' as Path<T>;
  const descPath = 'description' as Path<T>;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onGeneratePackageLink)} className="space-y-6" noValidate>
        <div className="max-w-[640px] flex flex-col gap-6 px-1 w-full">
          <FormField
            control={form.control}
            name={namePath}
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  htmlFor="agent-name"
                  className="text-sm font-semibold tracking-tight text-foreground mb-1.5 block"
                >
                  Agent Name
                </FormLabel>
                <FormControl>
                  <Input
                    id="agent-name"
                    autoComplete="off"
                    placeholder="eg: sap-standalone-prod"
                    {...field}
                    autoFocus
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={hostPath}
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  htmlFor="agent-host"
                  className="text-sm font-semibold tracking-tight text-foreground mb-1.5 block"
                >
                  Agent Host
                </FormLabel>
                <FormControl>
                  <Input id="agent-host" autoComplete="url" placeholder="eg: 10.0.1.25" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {children}

          <FormField
            control={form.control}
            name={descPath}
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  htmlFor="agent-description"
                  className="text-sm font-semibold tracking-tight text-foreground mb-1.5 block"
                >
                  Description (Optional)
                </FormLabel>
                <FormControl>
                  <Textarea
                    id="agent-description"
                    autoComplete="off"
                    placeholder="Description..."
                    className="min-h-[100px] resize-none"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <GenericButton variant="secondary" type="button" label="Close" onClick={onClose} />
          <GenericButton variant="default" type="submit" label="Generate Agent" isLoading={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};
